import { environment } from '@core/environment';
import { spatialRefState, xyToLngLat } from '@core/logic/helper';
import { GrpcRestFetchTransport, useAuth0Interceptor } from '@deepup/api-utils';
import { imapApiV1 as imapApi, imapClientApiV1 as imapClientApi } from '@deepup/internal-apis';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnapshot } from 'valtio';

type PageSize = {
  pageSize?: number;
};

type UseApiPublicLayersProps = {
  enabled?: boolean;
  viewPort?: typeof WORLD_VIEWPORT;
} & PageSize;

type UnpackedLayer = {
  url: string;
  name: string;
  layerName: string;
  sourceType: string;
  groupName?: string;
  tileSize?: number;
  wfsUrl?: string;
  wfsLayerName?: string;
};

export type LayerWithUrl = {
  id?: string;
  url?: string;
  name?: string;
  layerName?: string;
  sourceType?: string;
  groupName?: string;
  tileSize?: number;
  wfsUrl?: string;
  wfsLayerName?: string;
  childLayers?: readonly LayerWithUrl[];
};

export const WORLD_VIEWPORT = {
  min: { latitude: -90, longitude: -180, lat: -90, lon: -180 },
  max: { latitude: 90, longitude: 180, lat: 90, lon: 180 },
};

/**
 * Computes a coarse, quantized regional viewport from current camera coordinates.
 * Quantizing to a ~0.05° (~5 km) grid prevents camera zooms and small pan adjustments
 * from triggering new network requests, while properly matching state/regional geofences.
 */
export const useRegionViewport = (): typeof WORLD_VIEWPORT | undefined => {
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const selectedProjectId = useSelector(viewer3dSelectors.selectedProjectId);
  const { lngLatToUTM } = useSnapshot(spatialRefState);
  const [regionViewport, setRegionViewport] = useState<typeof WORLD_VIEWPORT | undefined>(
    undefined,
  );
  const prevQuantizedRef = useRef<{ lat: number; lng: number; projectId?: string } | null>(null);

  useEffect(() => {
    if (!lngLatToUTM) {
      return;
    }

    if (
      !cameraViewport ||
      (cameraViewport.minX === 0 &&
        cameraViewport.maxX === 0 &&
        cameraViewport.minY === 0 &&
        cameraViewport.maxY === 0)
    ) {
      return;
    }

    try {
      const centerX = (cameraViewport.minX + cameraViewport.maxX) / 2;
      const centerY = (cameraViewport.minY + cameraViewport.maxY) / 2;
      const center = xyToLngLat({ x: centerX, y: centerY });

      if (
        !Number.isFinite(center.lat) ||
        !Number.isFinite(center.lng) ||
        (center.lat === 0 && center.lng === 0)
      ) {
        return;
      }

      const GRID = 0.05;
      const quantizedLat = Math.round(center.lat / GRID) * GRID;
      const quantizedLng = Math.round(center.lng / GRID) * GRID;

      if (
        prevQuantizedRef.current &&
        prevQuantizedRef.current.projectId === selectedProjectId &&
        prevQuantizedRef.current.lat === quantizedLat &&
        prevQuantizedRef.current.lng === quantizedLng
      ) {
        return;
      }

      prevQuantizedRef.current = {
        lat: quantizedLat,
        lng: quantizedLng,
        projectId: selectedProjectId,
      };

      setRegionViewport({
        min: {
          latitude: quantizedLat - GRID,
          longitude: quantizedLng - GRID,
          lat: quantizedLat - GRID,
          lon: quantizedLng - GRID,
        },
        max: {
          latitude: quantizedLat + GRID,
          longitude: quantizedLng + GRID,
          lat: quantizedLat + GRID,
          lon: quantizedLng + GRID,
        },
      });
    } catch {
      // ignore
    }
  }, [
    lngLatToUTM,
    selectedProjectId,
    cameraViewport?.minX,
    cameraViewport?.maxX,
    cameraViewport?.minY,
    cameraViewport?.maxY,
  ]);

  return regionViewport;
};

export const useApiPublicLayers = ({
  pageSize = 100,
  enabled = true,
  viewPort: customViewPort,
}: UseApiPublicLayersProps) => {
  const { i18n } = useTranslation();
  const authInterceptor = useAuth0Interceptor();
  const [unpackedLayers, setUnpackedLayers] = useState<UnpackedLayer[]>([]);
  const autoRegionViewport = useRegionViewport();
  const effectiveViewport = customViewPort ?? autoRegionViewport;

  const client = useMemo(() => {
    const transportRest = new GrpcRestFetchTransport({
      baseUrl: environment.unifiedApiUrl,
      fetchInit: {
        credentials: 'include',
      },
      interceptors: [authInterceptor],
    });

    return new imapClientApi.InternalMapServiceClient(transportRest);
  }, [authInterceptor]);

  const languageCode = i18n.language;

  // Recursive function to unpack layers and find layers with URLs
  const unpackLayers = (layerItems: imapApi.MapLayer[]): UnpackedLayer[] => {
    const result: UnpackedLayer[] = [];

    const processLayer = (layer: LayerWithUrl, parentId?: string) => {
      // If layer has a URL, add it to results
      if (layer.url) {
        result.push({
          url: layer.url,
          name: layer.name || '',
          layerName: layer.layerName || layer.name || '',
          sourceType: layer.sourceType || '',
          groupName: parentId || '',
          tileSize: layer.tileSize,
          wfsUrl: layer.wfsUrl,
          wfsLayerName: layer.wfsLayerName,
        });
      }

      // Recursively process child layers, passing current layer's ID as parent
      if (layer.childLayers && Array.isArray(layer.childLayers)) {
        layer.childLayers.forEach((childLayer) => processLayer(childLayer, layer.id || layer.name));
      }
    };

    layerItems.forEach((layer) => processLayer(layer as LayerWithUrl));
    return result;
  };

  useEffect(() => {
    if (!enabled || !effectiveViewport) return;

    let isMounted = true;
    const fetchLayers = async () => {
      try {
        const response = await client.listPublicLayers({
          languageCode,
          viewPort: effectiveViewport,
          options: {
            withPaginationInfo: true,
            contextId: '',
            pageSize,
            page: 0n,
          },
        }).response;

        if (isMounted) {
          const unpacked = unpackLayers(response.items);
          setUnpackedLayers(unpacked);
        }
      } catch (error) {
        console.error('Error fetching layers:', error);
      }
    };

    fetchLayers();

    return () => {
      isMounted = false;
    };
  }, [client, languageCode, pageSize, enabled, effectiveViewport]);

  return unpackedLayers;
};
